import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { JOB_BOARDS } from '../constants/jobBoards';
import BrandLogos from '../components/BrandLogos';
import {
  Briefcase, Users, Video, CheckCircle2, Send, XCircle, UserPlus, Megaphone, Info, Layers, Activity as ActivityIcon, Globe, Download, CalendarClock, CircleDot, ChevronDown, ChevronRight,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const BOARD_BY_ID = Object.fromEntries(JOB_BOARDS.map((b) => [b.id, b]));

const boardDisplay = (key) => {
  if (key === 'company-website') return { name: 'Company Website', country: 'Direct applies', color: '#2563eb', logoUrl: null, inlineGlyph: null };
  return BOARD_BY_ID[key] || { name: key, country: '', color: '#64748b', logoUrl: null, inlineGlyph: null };
};

const scoreCls = (s) =>
  s == null ? 'bg-slate-100 text-slate-500'
  : s >= 80 ? 'bg-emerald-100 text-emerald-700'
  : s >= 60 ? 'bg-amber-100 text-amber-700'
  : 'bg-rose-100 text-rose-700';

const STAGE_LABEL = {
  pending: 'New', reviewing: 'Screening', shortlisted: 'Jobasti Interview', interviewed: 'Interview (HM)',
  onsite_interview: 'Onsite', decision: 'Decision', offered: 'Offer', accepted: 'Hired',
  rejected: 'Rejected', withdrawn: 'Withdrawn',
};

const ACT_META = {
  application_received: { icon: UserPlus, cls: 'bg-blue-50 text-blue-600', line: (a) => `${a.candidateName || 'A candidate'} applied for ${a.roleTitle || 'a role'}` },
  interview_sent: { icon: Send, cls: 'bg-indigo-50 text-indigo-600', line: (a) => `${a.actorName || 'A recruiter'} sent a ${a.interviewType === 'async' ? 'async' : 'live'} interview invite to ${a.candidateName || 'a candidate'} for ${a.roleTitle || 'a role'}` },
  interview_had: { icon: Video, cls: 'bg-purple-50 text-purple-600', line: (a) => `${a.interviewType === 'async' ? 'Video' : 'Live'} interview held with ${a.candidateName || 'a candidate'} for ${a.roleTitle || 'a role'}` },
  role_published: { icon: Megaphone, cls: 'bg-emerald-50 text-emerald-600', line: (a) => `${a.actorName || 'A recruiter'} published ${a.roleTitle || 'a role'}` },
  contract_sent: { icon: CheckCircle2, cls: 'bg-green-50 text-green-600', line: (a) => `${a.actorName || 'A recruiter'} sent a contract to ${a.candidateName || 'a candidate'} for ${a.roleTitle || 'a role'}` },
  rejection_sent: { icon: XCircle, cls: 'bg-rose-50 text-rose-600', line: (a) => `${a.actorName || 'A recruiter'} sent a rejection to ${a.candidateName || 'a candidate'} for ${a.roleTitle || 'a role'}` },
};

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="border-slate-200 shadow-sm rounded-xl">
    <CardContent className="p-4 flex items-center gap-3">
      <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></span>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const RecruiterPipelines = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const orgId = userProfile?.organizationId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleId, setRoleId] = useState('all');
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  const [expandedBoards, setExpandedBoards] = useState({});

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    axios
      .get(`${API}/api/analytics/company/${orgId}/pipelines`)
      .then((r) => setData(r.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [orgId]);

  const roles = data?.roles || [];
  const scoped = roleId !== 'all';

  const applications = useMemo(
    () => (data?.applications || []).filter((a) => !scoped || a.positionId === roleId),
    [data, roleId, scoped]
  );
  const interviews = useMemo(
    () => (data?.interviews || []).filter((i) => !scoped || i.positionId === roleId),
    [data, roleId, scoped]
  );
  const activity = useMemo(
    () => (data?.activity || [])
      .filter((a) => !scoped || a.positionId === roleId)
      .filter((a) => recruiterFilter === 'all' || a.actorId === recruiterFilter),
    [data, roleId, recruiterFilter, scoped]
  );

  const kpis = useMemo(() => {
    if (!scoped) {
      const t = data?.totals || {};
      return { activeRoles: t.activeRoles || 0, applications: t.applications || 0, sent: t.interviewsSent || 0, had: t.interviewsHad || 0 };
    }
    return {
      applications: applications.length,
      sent: interviews.length,
      had: interviews.filter((i) => i.had).length,
    };
  }, [scoped, data, applications, interviews]);

  const selectedRole = useMemo(() => roles.find((r) => r.id === roleId) || null, [roles, roleId]);

  const exportCsv = () => {
    const rows = [['Name', 'Email', 'Role', 'Stage', 'Match %', 'Applied']];
    applications.forEach((a) => {
      rows.push([
        `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || 'Applicant',
        a.email || '',
        a.positionTitle || '',
        STAGE_LABEL[(a.status || '').toLowerCase()] || a.status || '',
        a.matchScore != null ? String(a.matchScore) : '',
        a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : '',
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const roleTag = scoped && selectedRole ? selectedRole.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : 'all-roles';
    link.download = `pipeline-${roleTag}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const boards = data?.boards || [];

  const exportBtn = (
    <button
      onClick={exportCsv}
      disabled={loading || applications.length === 0}
      className="inline-flex items-center gap-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-3.5 py-2 rounded-lg transition-colors"
      data-testid="pipelines-export-csv"
    >
      <Download className="w-4 h-4" /> Export CSV
    </button>
  );

  return (
    <RecruiterLayout title="Pipelines" subtitle="Roles, candidates, boards & activity — all in one place" headerRight={exportBtn}>
      <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-6" data-testid="recruiter-pipelines-page">
        {loading ? (
          <p className="text-sm text-slate-400 py-16 text-center">Loading pipelines…</p>
        ) : !data ? (
          <p className="text-sm text-slate-400 py-16 text-center">Could not load pipeline data.</p>
        ) : (
          <>
            {/* Role filter */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-medium text-slate-600">Filter by job role</label>
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[260px]"
                data-testid="pipelines-role-filter"
              >
                <option value="all">All roles ({roles.length})</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.title} · {r.applications} apps</option>
                ))}
              </select>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pipelines-kpis">
              {scoped ? (
                <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="pipelines-role-status">
                  <CardContent className="p-4 flex items-center gap-3">
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedRole?.status === 'published' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                      <CircleDot className="w-5 h-5" />
                    </span>
                    <div>
                      <p className={`text-2xl font-bold leading-none ${selectedRole?.status === 'published' ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {selectedRole?.status === 'published' ? 'Active' : 'Inactive'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Role status</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <KpiCard icon={Briefcase} label="Active roles" value={kpis.activeRoles} accent="bg-blue-50 text-blue-600" />
              )}
              <KpiCard icon={Users} label="Applications" value={kpis.applications} accent="bg-pink-50 text-pink-600" />
              <KpiCard icon={Send} label="Interviews sent" value={kpis.sent} accent="bg-indigo-50 text-indigo-600" />
              <KpiCard icon={Video} label="Interviews had" value={kpis.had} accent="bg-purple-50 text-purple-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Candidates & Applications */}
              <div className="lg:col-span-7">
                <Card className="border-slate-200 shadow-sm rounded-xl h-full">
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" /> Candidates &amp; Applications
                      <span className="text-xs font-medium text-slate-400">({applications.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {applications.length === 0 ? (
                      <p className="text-sm text-slate-400 py-10 text-center">No applications {scoped ? 'for this role' : 'yet'}.</p>
                    ) : (
                      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto" data-testid="pipelines-applications">
                        {applications.map((a) => (
                          <button
                            key={a.applicationId}
                            onClick={() => navigate(`/candidates/${a.applicationId}`)}
                            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-blue-50/60 transition-colors"
                            data-testid={`pipeline-application-${a.applicationId}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-900 truncate">{`${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || 'Applicant'}</p>
                              <p className="text-xs text-slate-500 truncate">{a.positionTitle}</p>
                            </div>
                            <span className="text-[11px] text-slate-400 hidden sm:block">{STAGE_LABEL[(a.status || '').toLowerCase()] || a.status}</span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${scoreCls(a.matchScore)}`}>
                              {a.matchScore != null ? `${a.matchScore}%` : 'n/a'}
                            </span>
                            <span className="text-[11px] text-slate-400 whitespace-nowrap hidden md:block">
                              {a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Interviews */}
              <div className="lg:col-span-5">
                <Card className="border-slate-200 shadow-sm rounded-xl h-full">
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Video className="w-4 h-4 text-purple-600" /> Interviews
                      <span className="text-xs font-medium text-slate-400">{interviews.filter((i) => i.had).length} had · {interviews.length} sent</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {interviews.length === 0 ? (
                      <p className="text-sm text-slate-400 py-10 text-center">No interviews {scoped ? 'for this role' : 'yet'}.</p>
                    ) : (
                      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto" data-testid="pipelines-interviews">
                        {interviews.map((i) => (
                          <div key={i.id} className="flex items-center gap-3 px-4 py-3" data-testid={`pipeline-interview-${i.id}`}>
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${i.interviewType === 'async' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                              <Video className="w-4 h-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-800 truncate">{i.positionTitle}</p>
                              <p className="text-xs text-slate-500">{i.interviewType === 'async' ? 'Video' : 'Live'}{i.recruiterName ? ` · ${i.recruiterName}` : ''}</p>
                            </div>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${i.had ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                              {i.had ? 'Had' : 'Scheduled'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Job boards (estimated) */}
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-600" /> Job boards
                  </CardTitle>
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded-full" data-testid="pipelines-estimated-note">
                    <Info className="w-3 h-3" /> Board attribution is estimated (source tracking coming soon)
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="pipelines-boards">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3 font-semibold">Job Board</th>
                        <th className="py-2 px-3 font-semibold text-center">Active roles</th>
                        <th className="py-2 px-3 font-semibold text-center">Applications</th>
                        <th className="py-2 px-3 font-semibold text-center">Interviews</th>
                        <th className="py-2 pl-3 font-semibold text-right">Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boards.map((b) => {
                        const disp = boardDisplay(b.boardKey);
                        const boardRoles = Array.isArray(b.roles) ? b.roles : [];
                        const isOpen = !!expandedBoards[b.boardKey];
                        return (
                          <React.Fragment key={b.boardKey}>
                            <tr
                              className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                              onClick={() => setExpandedBoards((s) => ({ ...s, [b.boardKey]: !s[b.boardKey] }))}
                              data-testid={`pipeline-board-${b.boardKey}`}
                            >
                              <td className="py-3 pr-3">
                                <div className="flex items-center gap-2.5">
                                  {disp.inlineGlyph && BrandLogos[disp.inlineGlyph] ? (
                                    React.createElement(BrandLogos[disp.inlineGlyph], { size: 28 })
                                  ) : disp.logoUrl ? (
                                    <img src={disp.logoUrl} alt={disp.name} className="w-7 h-7 object-contain" loading="lazy" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-[11px]" style={{ backgroundColor: disp.color }}>
                                      {disp.name.charAt(0)}
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900 truncate leading-tight">{disp.name}</p>
                                    <p className="text-[11px] text-slate-400 flex items-center gap-1"><Globe className="w-3 h-3" /> {disp.country}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-3 text-center font-semibold text-slate-800">{b.activeRoleCount}</td>
                              <td className="py-3 px-3 text-center font-semibold text-slate-800">{b.applications}{b.estimated ? '~' : ''}</td>
                              <td className="py-3 px-3 text-center font-semibold text-slate-800">{b.interviews}{b.estimated ? '~' : ''}</td>
                              <td className="py-3 pl-3 text-right">
                                <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                                  {boardRoles.length} {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                </span>
                              </td>
                            </tr>
                            {isOpen && boardRoles.length > 0 && (
                              <tr className="bg-slate-50/50" data-testid={`board-roles-${b.boardKey}`}>
                                <td colSpan={5} className="px-4 py-2">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
                                        <th className="py-1.5 pr-3 font-medium">Role</th>
                                        <th className="py-1.5 px-3 font-medium">Status</th>
                                        <th className="py-1.5 px-3 font-medium text-center">Applications</th>
                                        <th className="py-1.5 pl-3 font-medium text-right">Days active</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {boardRoles.map((r) => (
                                        <tr key={r.id} className="border-t border-slate-100">
                                          <td className="py-1.5 pr-3 text-slate-700">{r.title}</td>
                                          <td className="py-1.5 px-3">
                                            <span className={`inline-flex items-center gap-1 ${r.active ? 'text-emerald-600' : 'text-slate-400'}`}>
                                              <span className={`w-1.5 h-1.5 rounded-full ${r.active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                              {r.active ? 'Active' : 'Inactive'}
                                            </span>
                                          </td>
                                          <td className="py-1.5 px-3 text-center text-slate-600">{r.applications}{b.estimated ? '~' : ''}</td>
                                          <td className="py-1.5 pl-3 text-right text-slate-500">
                                            <span className="inline-flex items-center gap-0.5"><CalendarClock className="w-3 h-3" />{r.daysActive}d</span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {boards.length <= 1 && (
                  <p className="text-xs text-slate-400 mt-3">Publish roles to job boards (via Add / Edit position) to see a per-board breakdown here.</p>
                )}
              </CardContent>
            </Card>

            {/* Activity */}
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <ActivityIcon className="w-4 h-4 text-blue-600" /> Activity
                    <span className="text-xs font-medium text-slate-400">({activity.length})</span>
                  </CardTitle>
                  <select
                    value={recruiterFilter}
                    onChange={(e) => setRecruiterFilter(e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="pipelines-recruiter-filter"
                  >
                    <option value="all">All recruiters</option>
                    {(data.recruiters || []).map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {activity.length === 0 ? (
                  <p className="text-sm text-slate-400 py-10 text-center">No activity {recruiterFilter !== 'all' ? 'for this recruiter' : ''} {scoped ? 'on this role' : ''}.</p>
                ) : (
                  <div className="space-y-1 max-h-[440px] overflow-y-auto" data-testid="pipelines-activity">
                    {activity.map((a) => {
                      const meta = ACT_META[a.type] || { icon: ActivityIcon, cls: 'bg-slate-50 text-slate-500', line: () => a.type };
                      const Icon = meta.icon;
                      return (
                        <div key={a.id} className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0" data-testid={`pipeline-activity-${a.id}`}>
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.cls}`}><Icon className="w-4 h-4" /></span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-700 leading-snug">{meta.line(a)}</p>
                            <p className="text-[11px] text-slate-400">{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </RecruiterLayout>
  );
};

export default RecruiterPipelines;
